import { ListBaseModel } from '@/api/models';
import { DateRangeSchema } from '@/utils';
import { z } from 'zod';
import { AccountTitleBaseModel } from './account-title';
import { AccountingTypeModel } from './accounting-type';
import { ExpenditureBudgetJurisdictionModel } from './expenditure-budget-jurisdiction';
import { ExpenditureBudgetOrganizationModel } from './expenditure-budget-organization';
import { ExpenditurePurposeModel } from './expenditure-purpose';

const ExpenditureBudgetPurposeBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  jurisdictionId: z.string().uuid(),
  organizationId: z.string().uuid().nullish(),
  accountingId: z.string().uuid(),
  accountTitleId: z.string().uuid().nullish(),
  expenditurePurposeId: z.string().uuid(),
});

export const ExpenditureBudgetPurposeModel =
  ExpenditureBudgetPurposeBaseModel.extend({
    jurisdiction: ExpenditureBudgetJurisdictionModel,
    organization: ExpenditureBudgetOrganizationModel.nullable(),
    accountingType: AccountingTypeModel,
    accountTitle: AccountTitleBaseModel.nullable(),
    expenditurePurpose: ExpenditurePurposeModel,
  });

const ExpenditureBudgetPurposeChildrenModel = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  code: z.number().int(),
  expenditureBudgetJurisdictionId: z.string().uuid().nullish(),
  expenditureBudgetOrganizationId: z.string().uuid().nullish(),
  accountingId: z.string().uuid().nullish(),
  accountTitleId: z.string().uuid().nullish(),
  expenditureBudgetPurposeId: z.string().uuid().nullable(),
  expenditureBudgetPurposeStartYear: z.number().int().nullish(),
  expenditureBudgetPurposeEndYear: z.number().int().nullish(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

type ExpenditureBudgetPurposeChildrenType = z.infer<
  typeof ExpenditureBudgetPurposeChildrenModel
> & {
  children: ExpenditureBudgetPurposeChildrenType[];
};

const RecursiveExpenditureBudgetPurposeChildrenModel: z.ZodType<ExpenditureBudgetPurposeChildrenType> =
  ExpenditureBudgetPurposeChildrenModel.extend({
    children: z.lazy(() =>
      RecursiveExpenditureBudgetPurposeChildrenModel.array(),
    ),
  });

const RecursiveExpenditureBudgetPurposeBaseModel = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  code: z.number().int(),
  children: RecursiveExpenditureBudgetPurposeChildrenModel.array(),
});

export const ExpenditureBudgetPurposesNestedExpenditurePurposeModel =
  ListBaseModel.extend({
    results: RecursiveExpenditureBudgetPurposeBaseModel.array(),
  });

const OptionModel = z.object({
  label: z.string(),
  value: z.string().uuid(),
  /**
   * 歳出項目の場合のみ設定されている。会計・組織・所管はundefined
   * 1: 項
   * 2: 目
   * */
  level: z.number().int().nullish(),
});

export type NestedExpenditureBudgetPurposeType = z.infer<typeof OptionModel> & {
  children: NestedExpenditureBudgetPurposeType[];
};

const NestedExpenditureBudgetPurposeBaseModel: z.ZodType<NestedExpenditureBudgetPurposeType> =
  OptionModel.extend({
    children: z.lazy(() => NestedExpenditureBudgetPurposeBaseModel.array()),
  });

export const NestedExpenditureBudgetPurposeModel =
  NestedExpenditureBudgetPurposeBaseModel.array();

export const ExpenditureBudgetPurposeFormSchema =
  ExpenditureBudgetPurposeBaseModel.pick({
    jurisdictionId: true,
    organizationId: true,
    accountingId: true,
    accountTitleId: true,
  }).extend({
    period: DateRangeSchema.nullish(),
    expenditurePurposeIds:
      ExpenditureBudgetPurposeBaseModel.shape.expenditurePurposeId
        .array()
        .min(1),
  });

export const ExpenditureBudgetPurposeSearchFormSchema = z.object({
  accountingId: z.string().uuid(),
  accountTitleId: z.string().uuid().nullable(),
  expenditureBudgetJurisdictionId: z.string().uuid(),
  expenditureBudgetOrganizationId: z.string().uuid().nullable(),
});
